import { ADD_ON_PUBLISHED } from './addOnModel';
import type { AddOn, AddOnAnchor } from './addOnModel';
import { DEFAULT_ADD_ON_TYPE } from './addOnType';

export const TEXT_FIELDS = ['pretitle', 'title', 'author', 'byLine', 'body', 'imageUrl', 'imageCaption'] as const;

type AddOnTextField = (typeof TEXT_FIELDS)[number];

// The lengths the host keeps (Farfalla's add-on validation), so nothing the
// reader sends comes back refused over them.
export const ADD_ON_FIELD_LIMITS = {
    title: 255,
    pretitle: 255,
    author: 255,
    byLine: 1000,
    imageCaption: 2000,
} as const satisfies Partial<Record<AddOnTextField, number>>;

// The host counts the body's characters without its markup.
export const ADD_ON_BODY_TEXT_LIMIT = 60000;

// The viewer shows these when present, so an empty input means "none", not "".
const OPTIONAL_FIELDS: readonly string[] = ['pretitle', 'author', 'byLine', 'imageUrl', 'imageCaption'];

/**
 * What the form holds while it is open. `typeAuto` is the picker's own state
 * and never reaches a record, and `type` is null while it is on,
 * because the answer is then read off the content
 * rather than stored beside it.
 */
export type AddOnDraft = Record<AddOnTextField, string> & {
    type: string | null;
    typeAuto: boolean;
    anchor: AddOnAnchor | null;
    status: string;
    /** An image chosen on this device, which the host stores in place of one named by URL. */
    imageFile: File | null;
};

/** A draft as the store takes it, with no id until a new piece is saved. */
export type AddOnDraftRecord = Record<AddOnTextField, string | null> & {
    id: string | number | null;
    type: string;
    anchor: AddOnAnchor | null;
    status: string;
    imageFile: File | null;
};

/** A piece nobody has stored yet, under the uuid every try to store it repeats. */
export type NewAddOnRecord = AddOnDraftRecord & { uuid: string };

// A record carries an unwritten field as null, and both the type reader and the
// form need text there.
const asText = (value: unknown): string => (value == null ? '' : String(value));

const textFrom = (source: Record<string, unknown>): Record<AddOnTextField, string> =>
    Object.fromEntries(TEXT_FIELDS.map(field => [field, asText(source[field])])) as Record<AddOnTextField, string>;

// A copy each way, so a draft the editor still holds cannot reach into a stored anchor.
const anchorCopy = (anchor: AddOnAnchor | null | undefined): AddOnAnchor | null => (anchor ? { ...anchor } : null);

export function emptyAddOnDraft({ anchor }: { anchor?: AddOnAnchor | null }): AddOnDraft {
    return {
        ...textFrom({}),
        type: null,
        typeAuto: true,
        anchor: anchorCopy(anchor),
        imageFile: null,
        // Explicit, because a record that reaches the store without a status publishes.
        status: ADD_ON_PUBLISHED,
    };
}

export function draftFromAddOn(addOn: AddOn): AddOnDraft {
    return {
        ...textFrom(addOn),
        type: asText(addOn.type) || DEFAULT_ADD_ON_TYPE,
        // An existing icon never changes silently: readers have learned it.
        typeAuto: false,
        anchor: anchorCopy(addOn.anchor),
        imageFile: null,
        // Only a missing status publishes. Every other value, empty included, is
        // the visibility gate's to read.
        status: addOn.status == null ? ADD_ON_PUBLISHED : asText(addOn.status),
    };
}

/**
 * The record the store takes. The effective type is the caller's to
 * resolve: while the picker is automatic the draft holds none, and
 * the answer is read off the content it names. A caller that
 * publishes names the status. Otherwise the draft's own
 * status stands.
 */
export function recordFromDraft(
    draft: AddOnDraft,
    id: string | number | null,
    type: string,
    { status }: { status?: string } = {}
): AddOnDraftRecord {
    const text = Object.fromEntries(
        TEXT_FIELDS.map(field => {
            const value = asText(draft[field]).trim();
            return [field, OPTIONAL_FIELDS.includes(field) && !value ? null : value];
        })
    ) as Record<AddOnTextField, string | null>;

    return {
        ...text,
        id: id ?? null,
        type,
        anchor: anchorCopy(draft.anchor),
        imageFile: draft.imageFile ?? null,
        status: status ?? draft.status ?? ADD_ON_PUBLISHED,
    };
}
